import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Sun, Moon } from "lucide-react";
import { API } from "../utils/helpers";
import ModernLayout from "./ModernLayout";
import ClassicLayout from "./ClassicLayout";

const ModernClock = ({ children }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return children(now);
};

const getCachedTheme = (profile) => {
  try {
    const cached = localStorage.getItem(`qen_theme_${profile}`);
    if (cached) return JSON.parse(cached);
  } catch {}
  return { theme: "modern", layout: "modern" };
};

const getProTheme = (profile) => {
  try {
    const v = localStorage.getItem(`qen_pro_theme_${profile}`);
    if (v === 'light' || v === 'dark') return v;
  } catch {}
  return 'light'; // profesyonel tema varsayilan olarak acik
};

const PricePage = ({ profile = "default", tvMode = false }) => {
  const cached = getCachedTheme(profile);
  const [prices, setPrices] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [theme, setTheme] = useState(cached.theme);
  const [layout, setLayout] = useState(cached.layout);
  const [proTheme, setProTheme] = useState(getProTheme(profile));
  const [aksoPrices, setAksoPrices] = useState(null);
  const [goldPrices, setGoldPrices] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [priceDirections, setPriceDirections] = useState({});
  const [tickerSettings, setTickerSettings] = useState({ mode: 'prices', custom_text: '' });
  const prevPricesRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    document.body.classList.forEach(cls => {
      if (cls.startsWith('theme-')) document.body.classList.remove(cls);
    });
    const isProInterface = theme === 'proauto';
    const effective = isProInterface ? `theme-pro-${proTheme}` : 'theme-modern';
    document.body.classList.add(effective);
    if (tvMode) {
      document.body.classList.add('tv-mode');
    } else {
      document.body.classList.remove('tv-mode');
    }
    return () => document.body.classList.remove('tv-mode');
  }, [theme, tvMode, proTheme]);

  const changeProTheme = (v) => {
    setProTheme(v);
    try { localStorage.setItem(`qen_pro_theme_${profile}`, v); } catch {}
  };

  const fetchPrices = async (first = false) => {
    try {
      const baseUrl = profile === "default" ? `${API}/prices/all` : `${API}/prices/all?profile=${profile}`;
      const sep = baseUrl.includes('?') ? '&' : '?';
      const url = `${baseUrl}${sep}_t=${Date.now()}`;
      const res = await axios.get(url, { headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' } });
      const newPrices = res.data;

      if (prevPricesRef.current) {
        const dirs = {};
        const prev = prevPricesRef.current;
        const cmp = (k, n, o) => { if (n && o && n !== o) dirs[k] = n > o ? 'up' : 'down'; };
        (newPrices?.gram_altin || []).forEach(g => {
          const o = (prev?.gram_altin || []).find(p => p.gram === g.gram);
          if (o) { cmp(`gram_${g.gram}_alis`, g.qen_alis, o.qen_alis); cmp(`gram_${g.gram}_satis`, g.qen_satis, o.qen_satis); }
        });
        (newPrices?.sarrafiye || []).forEach(s => {
          const o = (prev?.sarrafiye || []).find(p => p.name === s.name);
          if (o) { cmp(`sar_${s.name}_alis`, s.qen_alis, o.qen_alis); cmp(`sar_${s.name}_satis`, s.qen_satis, o.qen_satis); }
        });
        cmp('has_alis', newPrices?.has_altin?.qen_alis, prev?.has_altin?.qen_alis);
        cmp('has_satis', newPrices?.has_altin?.qen_satis, prev?.has_altin?.qen_satis);
        cmp('bilezik_alis', newPrices?.bilezik?.alis, prev?.bilezik?.alis);
        if (Object.keys(dirs).length > 0) setPriceDirections(p => ({ ...p, ...dirs }));
      }
      prevPricesRef.current = newPrices;
      setPrices(newPrices);
    } catch (err) {
      console.error("Fiyatlar yuklenemedi:", err);
    }
  };

  const fetchSettings = async () => {
    try {
      const ts = Date.now();
      const noCache = { headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' } };
      const [themeRes, tickerRes] = await Promise.all([
        axios.get(`${API}/admin/theme?profile=${profile}&_t=${ts}`, noCache).catch(() => null),
        axios.get(`${API}/admin/ticker-settings?profile=${profile}&_t=${ts}`, noCache).catch(() => null)
      ]);
      if (themeRes?.data) {
        const t = themeRes.data.theme || "modern";
        const l = themeRes.data.layout || "modern";
        setTheme(t);
        setLayout(l);
        localStorage.setItem(`qen_theme_${profile}`, JSON.stringify({ theme: t, layout: l }));
      }
      if (tickerRes?.data) setTickerSettings(tickerRes.data);
    } catch {}
  };

  const fetchAkso = async () => {
    try {
      const res = await axios.get(`${API}/akso/prices?_t=${Date.now()}`, { headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' } });
      setAksoPrices(res.data?.prices || res.data);
    } catch {}
  };

  const fetchGold = async () => {
    try {
      const res = await axios.get(`${API}/prices/gold?_t=${Date.now()}`, { headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' } });
      if (res.data?.success) {
        const newGold = res.data.data;
        if (goldPrices) {
          const dirs = {};
          const cmp = (k, n, o) => { if (n && o && n !== o) dirs[k] = n > o ? 'up' : 'down'; };
          cmp('ons_alis', newGold?.ons?.alis, goldPrices?.ons?.alis);
          cmp('ons_satis', newGold?.ons?.satis, goldPrices?.ons?.satis);
          cmp('usdkg_alis', newGold?.usdkg?.alis, goldPrices?.usdkg?.alis);
          cmp('usdkg_satis', newGold?.usdkg?.satis, goldPrices?.usdkg?.satis);
          cmp('eurkg_alis', newGold?.eurkg?.alis, goldPrices?.eurkg?.alis);
          cmp('eurkg_satis', newGold?.eurkg?.satis, goldPrices?.eurkg?.satis);
          if (Object.keys(dirs).length > 0) setPriceDirections(p => ({ ...p, ...dirs }));
        }
        setGoldPrices(newGold);
      }
    } catch {}
  };

  useEffect(() => {
    fetchPrices(true);
    fetchSettings();
    fetchAkso();
    fetchGold();
    const priceInt = setInterval(() => fetchPrices(false), 1000);
    const settingsInt = setInterval(fetchSettings, 10000);
    const aksoInt = setInterval(fetchAkso, 60000);
    const goldInt = setInterval(fetchGold, 1000);

    return () => { clearInterval(priceInt); clearInterval(settingsInt); clearInterval(aksoInt); clearInterval(goldInt); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  // TV/mobil cihazlarda sayfa tekrar gorunur olunca (background'dan donunce) hemen refresh et
  // NOT: Sadece bfcache restore (event.persisted) veya hidden->visible transition'da fire edilir.
  // Boylece sayfa mount'ta initial fetch ile catismayan extra paralel fetch yapilmaz.
  useEffect(() => {
    let lastRefresh = Date.now();
    const onVisible = (e) => {
      // Mount'tan hemen sonra fire eden duplicate event'leri 2sn debounce ile engelle
      if (Date.now() - lastRefresh < 2000) return;
      if (document.visibilityState !== 'visible') return;
      // pageshow event'i sadece bfcache restore'da (event.persisted=true) onemli
      if (e && e.type === 'pageshow' && !e.persisted) return;
      lastRefresh = Date.now();
      fetchPrices(false);
      fetchGold();
      fetchSettings();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener('pageshow', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener('pageshow', onVisible);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const isProInterface = theme === 'proauto';
  const sharedProps = {
    prices, gramPrices: prices?.gram_altin || [], hasAltin: prices?.has_altin,
    sarrafiyePrices: prices?.sarrafiye || [], aksoPrices, goldPrices, priceDirections,
    tickerSettings, profile, theme: isProInterface ? (proTheme === 'dark' ? 'lacivert' : 'modern') : 'modern', isMobile, historyOpen, setHistoryOpen, tvMode
  };

  const proToggle = (isProInterface && (
    <div data-testid="pro-theme-toggle" style={{
      position: 'fixed', top: 10, right: 10, zIndex: 20000, display: 'flex', gap: 4,
      background: 'rgba(0,0,0,0.4)', padding: 4, borderRadius: 999,
      backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
      boxShadow: '0 4px 16px rgba(0,0,0,0.25)'
    }}>
      {[{ v: 'light', Icon: Sun, t: 'Açık tema' }, { v: 'dark', Icon: Moon, t: 'Koyu tema' }].map(({ v, Icon, t }) => (
        <button key={v} title={t} data-testid={`pro-theme-${v}-btn`} onClick={() => changeProTheme(v)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 34, height: 34, borderRadius: 999, border: 'none', cursor: 'pointer',
            transition: 'background 0.2s ease, color 0.2s ease',
            background: proTheme === v ? '#D4AF37' : 'transparent',
            color: proTheme === v ? '#1a1a1a' : '#fff',
          }}>
          <Icon size={17} strokeWidth={2.2} />
        </button>
      ))}
    </div>
  ));

  const content = <ModernClock>{(now) => <ModernLayout {...sharedProps} now={now} />}</ModernClock>;

  return (<>{proToggle}{content}</>);
};

export default PricePage;
